import _isString from 'lodash/isString';
import _isObject from 'lodash/isObject';
import _size from 'lodash/size';
import _uniqueId from 'lodash/uniqueId';
import _forEach from 'lodash/forEach';
import BackboneEvents from 'backbone-events-standalone';

/**
  Class to publish/subscribe to dynamic data stores. This dispatcher is used to
  share data between web modules.
  This is an experimental utility module that will be spun off as a separate
  open-source project. We are going to keep this here and learn and experiment
  with the requirements.
*/
export default class DataStoreDispatcher {
  //TODO: 1. Move this to a separate repo with NPM 2. Write tests
  static _eventEmittersByDataStoreId = {};
  static _dataByDataStoreId = {};

  static _eventEmitter(dataStoreId) {
    let ee;

    if (DataStoreDispatcher._eventEmittersByDataStoreId[dataStoreId]) {
      ee = DataStoreDispatcher._eventEmittersByDataStoreId[dataStoreId];
    } else {
      ee = DataStoreDispatcher._eventEmittersByDataStoreId[dataStoreId] = BackboneEvents.mixin({});
    }

    return ee;
  }

  static _data(dataStoreId) {
    let data;

    if (DataStoreDispatcher._dataByDataStoreId[dataStoreId]) {
      data = DataStoreDispatcher._dataByDataStoreId[dataStoreId];
    } else {
      data = DataStoreDispatcher._dataByDataStoreId[dataStoreId] = {};
    }

    return data;
  }

  _id;
  _dataStoreId;
  _eventEmitter = null;
  _data = {};

  /**
  * Create a data store dispatcher.
  * @param {string} dataStoreId - ID of the data store
  */
  constructor (dataStoreId) {
    // TODO: add dataStoreId as part of the id
    this._id = _uniqueId('FRN_DataStoreDispatcher_');
    this._dataStoreId = dataStoreId;

    this._ee = DataStoreDispatcher._eventEmitter(dataStoreId);
    this._data = DataStoreDispatcher._data(dataStoreId);
  }

  /**
  * Gets the full data object.
  * @returns {Object} Returns the full data object.
  */
  /**
  * Gets the value at `key` of the data object.
  * @param {string} key Name of the property.
  * @returns {*} Returns the resolved value.
  */
  get() {
    const numberOfArguments = _size(arguments);

    if (numberOfArguments === 0) {
      return this._data;
    } else if (numberOfArguments === 1 && _isString(arguments[0])){
      const [ key ] = arguments;

      return this._data[key];
    } else {
      this._throwUnsupportedArguments();
    }
  }

  /**
  * Sets the full data object.
  * @param {Object} data The new data object to replace existing data object.
  */
  /**
  * Sets the value at `key` of the data object.
  * @param {string} key The name of the property to be set.
  * @param {*} val The value to set.
  */
  set() {
    const numberOfArguments = _size(arguments);

    if (numberOfArguments === 1 && _isObject(arguments[0])) {
      const [ data = DataStoreDispatcher._data(this._dataStoreId) ] = arguments;
      const changes = [];

      _forEach(data, (val, key) => {
        if (this._data[key] !== val) {
          this._data[key] = val;
          changes.push([key, val]);
        }
      });

      if (_size(changes) > 0) {
        _forEach(changes, (change) => {
          const [key, val] = change;
          this._ee.trigger('_change:' + key, val, this._data);
        });

        this._ee.trigger('_change', this._data);
      }
    } else if (numberOfArguments === 2 && _isString(arguments[0])) {
      const [ key, val ] = arguments;

      if (this._data[key] !== val) {
        this._data[key] = val;
        this._ee.trigger('_change:' + key, val, this._data);
      }

      this._ee.trigger('_change', this._data);
    } else {
      this._throwUnsupportedArguments();
    }
  }

  /**
  * Subscribes to changes to the data.
  * @param {DataStoreDispatcher~onChangeCallback} fn The callback to be invoked when the data changes.
  */
  /**
  * @callback DataStoreDispatcher~onChangeCallback
  * @param {Object} data The full data object.
  */
  /**
  * Subscribes to changes to the specific property of the data.
  * @param {string} key The name of the property to subscribe to.
  * @param {DataStoreDispatcher~onChangeCallbackWithKey} fn The callback to be invoked when the property value changes.
  */
  /**
  * @callback DataStoreDispatcher~onChangeCallbackWithKey
  * @param {*} val The new value of the subscribed property.
  * @param {Object} data The full data object.
  */
  onChange() {
    const numberOfArguments = _size(arguments);

    if (numberOfArguments === 1) {
      const [ fn = () => {} ] = arguments;

      this._ee.on('_change', fn, this._id);
    } else if (numberOfArguments === 2) {
      const [key, fn = () => {} ] = arguments;

      this._ee.on('_change:' + key, fn, this._id);
    }
  }

  /**
  * Stops all of the subscriptions to the data store.
  */
  off() {
    const numberOfArguments = _size(arguments);

    if (numberOfArguments === 0) {
      this._ee.off(null, null, this._id);
    } else {
      this._throwNotYetImplemented();
    }
  }

  _throwUnsupportedNumberOfArguments() {
    throw new Error('Unsupported number of arguments');
  }

  _throwUnsupportedArguments() {
    throw new Error('Unsupported arguments');
  }

  _throwNotYetImplemented() {
    throw new Error('Not yet implemented');
  }
}



// WEBPACK FOOTER //
// ./app/lib/frankly/DataStoreDispatcher.js